/**
 * Casamento de palavra-chave em comentário/DM do Instagram.
 *
 * Vive em `shared/` porque a tela usa a mesma função para mostrar, enquanto a
 * pessoa digita a campanha, se um comentário de exemplo dispararia ou não. Se
 * cliente e servidor divergissem, a prévia mentiria.
 *
 * O que a função tolera, e por quê — cada regra veio de comentário real que
 * não casava:
 *
 *  - Maiúsculas: "PREÇO", "Preço" e "preço" são a mesma coisa.
 *  - Acentos: "preco" tem que casar com "preço" e vice-versa. Teclado de
 *    celular, pressa, autocorretor — quem comenta não é consistente, e uma
 *    campanha chaveada em "preco" perderia metade dos comentários. A remoção
 *    de acento é feita nos DOIS lados, e só em letras latinas: em devanágari,
 *    tailandês ou japonês os diacríticos mudam a palavra.
 *  - Emoji e pontuação: "Preçoo!! 🔥🔥" vira "precoo" antes de comparar.
 *  - Palavra inteira com Unicode: `\b` do JavaScript só entende ASCII, então
 *    "preço" nunca seria "palavra inteira" com `\b`. Usamos lookaround com
 *    `\p{L}`/`\p{N}`.
 *  - O/0: palavra-chave numérica ("08", "17") casa com "O8" — mesma forma na
 *    fonte do celular, e o autocapitalize ainda troca "o" por "O". Só vale
 *    para chave puramente numérica, para "oi" não virar "0i".
 */

export type KeywordMatch = {
  matched: boolean;
  /** A chave como foi cadastrada (não a normalizada) — é o que o log mostra. */
  matchedKeyword: string | null;
};

const EMOJI_RE =
  /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{200D}\u{20E3}]/gu;

/**
 * Deixa só letras (de qualquer alfabeto), números, marcas combinantes e
 * espaço. As marcas (`\p{M}`) precisam sobreviver aqui: é `removerAcentos`
 * quem decide, por alfabeto, quais cair — tirá-las agora quebraria "señor"
 * digitado em forma decomposta em "sen or".
 */
export function limparTexto(texto: string): string {
  return texto
    .replace(EMOJI_RE, "")
    .replace(/[^\p{L}\p{N}\p{M}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Remove acentos SÓ de letras latinas.
 *
 * O atalho comum, `normalize("NFD").replace(/\p{M}/gu, "")`, apaga marcas de
 * todo alfabeto — e fora do latino elas carregam significado (vogais em
 * devanágari, dakuten em japonês, "й" virando "и" em cirílico). Aqui a marca
 * herda o alfabeto da letra-base anterior, e só cai se a base for latina.
 */
export function removerAcentos(texto: string): string {
  let saida = "";
  let baseLatina = false;
  for (const ch of texto.normalize("NFD")) {
    if (/\p{M}/u.test(ch)) {
      if (!baseLatina) saida += ch;
      continue;
    }
    baseLatina = /\p{Script=Latin}/u.test(ch);
    saida += ch;
  }
  return saida.normalize("NFC");
}

/** Forma canônica usada nos dois lados da comparação. */
export function normalizarParaComparacao(texto: string): string {
  return removerAcentos(limparTexto(texto)).toLowerCase();
}

function ehChaveNumerica(chaveLimpa: string): boolean {
  return /^[0-9o]+$/i.test(chaveLimpa);
}

function dobrarOZero(valor: string): string {
  return valor.replace(/o/gi, "0");
}

function escaparRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Diz se o texto dispara alguma das chaves.
 *
 * @param palavraInteira `true` exige a chave como palavra isolada ("link" não
 *   casa "linkado"); `false` aceita como pedaço.
 */
export function casarPalavrasChave(
  texto: string,
  chaves: string[],
  palavraInteira = true,
): KeywordMatch {
  if (!texto || chaves.length === 0) return { matched: false, matchedKeyword: null };

  const textoLimpo = normalizarParaComparacao(texto);
  if (!textoLimpo) return { matched: false, matchedKeyword: null };

  for (const chave of chaves) {
    const chaveLimpa = normalizarParaComparacao(chave);
    if (!chaveLimpa) continue;

    const numerica = ehChaveNumerica(chaveLimpa);
    const alvo = numerica ? dobrarOZero(textoLimpo) : textoLimpo;
    const procurada = numerica ? dobrarOZero(chaveLimpa) : chaveLimpa;

    if (palavraInteira) {
      const re = new RegExp(`(?<![\\p{L}\\p{N}])${escaparRegex(procurada)}(?![\\p{L}\\p{N}])`, "iu");
      if (re.test(alvo)) return { matched: true, matchedKeyword: chave };
    } else if (alvo.includes(procurada)) {
      return { matched: true, matchedKeyword: chave };
    }
  }

  return { matched: false, matchedKeyword: null };
}

/**
 * Limpa a lista digitada na tela: separa por vírgula/quebra de linha, tira
 * espaços e duplicatas (comparando na forma normalizada, para "Preço" e
 * "preco" não entrarem duas vezes), e limita o tamanho.
 */
export function normalizarListaDeChaves(entrada: string | string[], max = 20): string[] {
  const brutas = Array.isArray(entrada) ? entrada : entrada.split(/[,\n;]/);
  const vistas = new Set<string>();
  const saida: string[] = [];
  for (const bruta of brutas) {
    const chave = bruta.trim().slice(0, 60);
    if (!chave) continue;
    const forma = normalizarParaComparacao(chave);
    if (!forma || vistas.has(forma)) continue;
    vistas.add(forma);
    saida.push(chave);
    if (saida.length >= max) break;
  }
  return saida;
}
